/**
 * Call-path leaderboard entry.
 *
 * The smallest unit of invocation in the EasyNet ontology is the call
 * path `(agent, ability)` — the leaderboard ranks instances of this
 * pair by engineering metrics, not by social/agent metrics.
 *
 * Why a dedicated type instead of `AgentSummary` + ad-hoc fields:
 *
 *   - Engineering metrics (`runCount`, `successCount`, `avgElapsedMs`)
 *     are properties of the *ability instance*, not of the agent.
 *     The same agent can host many abilities, each with its own
 *     usage profile.
 *   - The current `AgentSummary` API only exposes social metrics.
 *     A future `/abilities/leaderboard` endpoint will return items
 *     in this exact shape; the component code is then unchanged.
 *   - Mocked data and live data share the same type so the swap is
 *     a one-line edit in the data source.
 */

import type { AgentStatus } from './agent';

/**
 * Top-level partition for the call-path leaderboard. Coarser than
 * `abilityTags` — there are a handful of these and every ability
 * belongs to exactly one. The leaderboard's first drill-down level
 * groups by category.
 */
export type LeaderboardCategory =
  | 'chat'
  | 'code'
  | 'text'
  | 'vision'
  | 'planning'
  | 'team'
  | 'creative'
  | 'data';

export const LEADERBOARD_CATEGORIES: LeaderboardCategory[] = [
  'chat',
  'code',
  'text',
  'vision',
  'planning',
  'team',
  'creative',
  'data',
];

/** Display metadata for a category — icon name (Material Symbols)
 * and an i18n label key. Kept here so the UI doesn't have to invent
 * its own mapping. */
export const CATEGORY_META: Record<
  LeaderboardCategory,
  { icon: string; labelKey: string }
> = {
  chat:     { icon: 'forum',          labelKey: 'discover.categoryChat' },
  code:     { icon: 'code',           labelKey: 'discover.categoryCode' },
  text:     { icon: 'description',    labelKey: 'discover.categoryText' },
  vision:   { icon: 'image',          labelKey: 'discover.categoryVision' },
  planning: { icon: 'account_tree',   labelKey: 'discover.categoryPlanning' },
  team:     { icon: 'groups',         labelKey: 'discover.categoryTeam' },
  creative: { icon: 'palette',        labelKey: 'discover.categoryCreative' },
  data:     { icon: 'database',       labelKey: 'discover.categoryData' },
};

export interface LeaderboardCallPath {
  /** Stable id used as a React key. Format: `<agentId>::<abilitySlug>`. */
  id: string;

  // ─── Call path identity ─────────────────────────────────────────────
  agentId: string;
  agentName: string;
  agentAvatar: string;
  agentStatus: AgentStatus;
  /** Display name of the owning user/creator of the agent. */
  creatorName: string;

  /** Ability name as it appears in the call path. May contain `.` for
   * namespaced abilities (e.g. `group_work.gem`, `photo.capture`). */
  abilityName: string;
  /** One-line description of what the ability does. Surfaced in the row
   * subtitle so users can tell abilities apart at a glance. */
  abilityDescription: string;
  /** Free-form tags surfacing as small chips. Keep ≤3 visible. */
  abilityTags: string[];
  /** High-level category the ability belongs to (e.g. `code`, `text`,
   * `vision`, `planning`). Used as the top-level partition on the
   * leaderboard so users can drill from category → ability name →
   * agent rankings. Categories are coarser than tags: there are a
   * handful, and every ability belongs to exactly one. */
  category: LeaderboardCategory;

  // ─── Engineering metrics ───────────────────────────────────────────
  /** Total number of times the ability has been invoked. */
  runCount: number;
  /** Number of invocations that returned a non-error result. */
  successCount: number;
  /** Number of invocations that errored. `runCount = success + failure`. */
  failureCount: number;
  /** Mean elapsed time per invocation, in milliseconds. */
  avgElapsedMs: number;
  /** ISO timestamp of the most recent successful invocation. */
  lastInvokedAt: string;
  /** Average cost in USD per invocation. `null` if cost is not tracked. */
  avgCostUsd: number | null;
}

/**
 * Sort dimensions for the leaderboard. Each maps to a single column
 * the user can pivot on. Names mirror real engineering vocabulary:
 *   - `calls`: most invoked ability
 *   - `success`: highest success rate
 *   - `latency`: fastest mean response
 *   - `recency`: most recently active
 */
export type LeaderboardSort = 'calls' | 'success' | 'latency' | 'recency';

/** Compute success rate as a number in [0, 1]. Returns 0 for an ability
 * with no invocations to avoid divide-by-zero. */
export function successRate(entry: LeaderboardCallPath): number {
  if (entry.runCount === 0) return 0;
  return entry.successCount / entry.runCount;
}

/** Sort a list of entries in-place equivalents (returns a new array)
 * by the chosen dimension. Stable sort. */
export function sortCallPaths(
  entries: LeaderboardCallPath[],
  by: LeaderboardSort,
): LeaderboardCallPath[] {
  const sorted = [...entries];
  switch (by) {
    case 'calls':
      sorted.sort((a, b) => b.runCount - a.runCount);
      break;
    case 'success':
      sorted.sort((a, b) => successRate(b) - successRate(a));
      break;
    case 'latency':
      // Lower is better.
      sorted.sort((a, b) => a.avgElapsedMs - b.avgElapsedMs);
      break;
    case 'recency':
      sorted.sort((a, b) => b.lastInvokedAt.localeCompare(a.lastInvokedAt));
      break;
  }
  return sorted;
}
